import logger from '@/modules/Logger';
import store from '@/store/index';
import { DUAL_STREAM_MIN_BANDWIDTH, JOIN_TIMEOUT_MS, MAX_JOIN_ATTEMPTS } from '@/modules/webrtc/agora/const';
import { buildJoinMeta, joinPrefix } from '@/modules/webrtc/joinLogUtils';
import { selectInitialProfile } from '@/modules/webrtc/agora/properties/selectInitialProfile';
import { shouldEnableDualStreamOnJoin } from '@/modules/webrtc/agora/properties/shouldEnableDualStreamOnJoin';

export default {
  // initialize and join a channel
  async join(lessonId) {
    // channel name should be string
    this.channelName = String(lessonId);

    logger.info(
      `${joinPrefix('Agora')} Join started - channel: ${this.channelName}, userId: ${this.userId}`,
      buildJoinMeta({ provider: 'agora', lessonId })
    );

    this.configureAgoraLogs();
    this.setEventListener();

    // Select initial video profile based on measured bandwidth
    const measuredBandwidth = store.getters['videoRtc/measuredBandwidth'];
    const { profile, audioOnly } = selectInitialProfile(measuredBandwidth);

    logger.info(`[Agora] Profile: ${profile || 'audio-only'} | Bandwidth: ${measuredBandwidth} kbps`, {
      operation: 'webrtc:bandwidth-adaptive',
      measuredBandwidth,
      profile,
      audioOnly,
      lesson_id: lessonId
    });

    if (audioOnly) {
      // Very low bandwidth: skip video track creation, publish audio-only
      this.constraints.video = false;
      store.commit('videoRtc/setAudioOnly', true);
    } else {
      // Override the video profile with the bandwidth-selected profile
      this.videoProfile = profile;
    }

    // Gate dual stream on measured bandwidth so constrained networks defer the
    // sender-side upload spike; the runtime uplink upgrade path enables
    // dual-stream later when the link proves excellent.
    const enableDualStreamOnJoin = shouldEnableDualStreamOnJoin(measuredBandwidth, audioOnly);

    if (!enableDualStreamOnJoin) {
      logger.info('[Agora] Dual-stream deferred on join (bandwidth below threshold)', {
        operation: 'webrtc:bandwidth-adaptive',
        measuredBandwidth,
        threshold: DUAL_STREAM_MIN_BANDWIDTH,
        audioOnly,
        lesson_id: lessonId
      });
    }

    // DEV: expose adapter on window for mid-call testing via console
    window.__agoraAdapter__ = this;

    const safeLogLessonId = lessonId != null ? lessonId : 'unknown';
    let lastError = null;

    for (let attempt = 1; attempt <= MAX_JOIN_ATTEMPTS; attempt++) {
      try {
        const result = await this.joinWithTimeout(enableDualStreamOnJoin);

        if (attempt > 1) {
          try {
            logger.info('[Agora] Join succeeded after retry', {
              operation: 'webrtc:join',
              attempt,
              lesson_id: safeLogLessonId
            });
          } catch (_) { /* Requirement 7.6: logging must not interrupt the flow */ }
        }

        return result;
      } catch (error) {
        lastError = error;
        const retryable = this.isNetworkError(error);

        try {
          logger.error('[Agora] Join attempt failed', {
            operation: 'webrtc:join',
            attempt,
            max_attempts: MAX_JOIN_ATTEMPTS,
            retryable,
            err: error.code || error.message,
            event_type: 10,
            lesson_id: safeLogLessonId
          });
        } catch (_) { /* Requirement 7.6: logging must not interrupt the flow */ }

        // Forcefully cut off the in-progress/stale join before retrying.
        await this.cleanupBeforeRetry();

        // Stop retrying if the error is not recoverable or attempts are exhausted.
        if (!retryable || attempt === MAX_JOIN_ATTEMPTS) {
          break;
        }
      }
    }

    // All attempts exhausted (or non-retryable) — surface the error so the
    // component shows the existing "unexpected error, please refresh" message.
    return { error: lastError };
  },

  /**
   * Run a single join sequence and race it against a custom timeout.
   * If the join does not settle within JOIN_TIMEOUT_MS, the timeout wins and a
   * retryable NETWORK_TIMEOUT error is thrown so the retry loop kicks in.
   */
  async joinWithTimeout(enableDualStreamOnJoin) {
    let timeoutId = null;

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        const timeoutError = new Error(`Agora join timed out after ${JOIN_TIMEOUT_MS}ms`);
        // Tag with a code recognised by isNetworkError so the join is retried.
        timeoutError.code = 'NETWORK_TIMEOUT';
        reject(timeoutError);
      }, JOIN_TIMEOUT_MS);
    });

    const joinPromise = this.runJoinSequence(enableDualStreamOnJoin);

    try {
      return await Promise.race([joinPromise, timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // the actual join sequence: join channel, publish tracks, enable dual stream
  async runJoinSequence(enableDualStreamOnJoin) {
    // Capture the client reference at the start so this attempt's steps are
    // pinned to this client even if cleanupBeforeRetry() reassigns this.rtc.
    const client = this.rtc;

    // (1) join channel → peerId
    const peerId = await this.joinChannel();

    // (2) set device availability
    await this.setDeviceAvailability();

    // (3) publish stream to channel → stream
    const stream = await this.publishStreamToChannel();

    // (4) enable dual-stream mode (high/low quality stream) after publishing
    // tracks — Agora requires an active video track
    if (enableDualStreamOnJoin) {
      await this.enableDualStream();
    }

    return { peerId, stream, rtc: client };
  },

  /**
   * Clean up a hung or partially-completed join before the next attempt.
   * reset() closes local tracks, removes SDK listeners and leaves the channel,
   * which cancels any pending join on the current client. The reset is bounded
   * to 5 seconds — if it does not complete in time it is abandoned so a hung
   * teardown cannot stall the retry loop. A fresh client is then created and the
   * preserved event handlers are re-attached so the next attempt starts from a
   * clean state.
   */
  async cleanupBeforeRetry() {
    try {
      const CLEANUP_TIMEOUT_MS = 5000;

      const resetPromise = this.reset();
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve('CLEANUP_TIMED_OUT'), CLEANUP_TIMEOUT_MS);
      });

      const result = await Promise.race([resetPromise, timeoutPromise]);

      if (result === 'CLEANUP_TIMED_OUT') {
        try {
          logger.debug('[Agora] Reset timed out during join cleanup', {
            operation: 'webrtc:join-cleanup',
            reason: 'teardown timed out',
            lesson_id: store.state.lessonId || 'unknown'
          });
        } catch (_) { /* Requirement 7.6: logging must not interrupt the flow */ }
      }

      // reset() completed or was abandoned; recreate the client
      // and re-attach the persisted event handlers (this.events).
      this.rtc = this.createClient();
      this.setEventListener();
    } catch (error) {
      try {
        logger.debug('[Agora] Error during join cleanup before retry', {
          operation: 'webrtc:join-cleanup',
          reason: error.message,
          lesson_id: store.state.lessonId || 'unknown'
        });
      } catch (_) { /* Requirement 7.6: logging must not interrupt the flow */ }
    }
  }
}
